import { Chip } from "@tql/ui-components-react/kendo/buttons";
import type {
	AutoCompleteBlurEvent,
	AutoCompleteCloseEvent,
	AutoCompleteHandle,
	AutoCompleteProps,
} from "@tql/ui-components-react/kendo/dropdowns";
import { AutoComplete } from "@tql/ui-components-react/kendo/dropdowns";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { useDebounceValue } from "usehooks-ts";

import {
	useAddressBookItems,
	useAddressBookSearch,
	usePostalCodeSearch,
} from "~/features/address-book/hooks/use-address-book.ts";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useSharedListsAccess } from "~/shared/hooks/use-shared-lists-access.ts";
import { useUserConfiguration } from "~/shared/hooks/use-user-configuration.ts";
import type { AddressBookItemSummary } from "~/shared/schemas/address-book-schema.ts";
import { ConfigurationType } from "~/shared/schemas/config-schema.ts";
import type { ZipCode } from "~/shared/schemas/location-state-schema.ts";

const countryOrder: Record<string, number> = { USA: 1, CAN: 2, MEX: 3 };

export type SearchResult =
	| {
			type: "postalCode";
			data: ZipCode;
			displayValue: string;
			group?: string;
	  }
	| {
			type: "addressBook";
			data: AddressBookItemSummary;
			displayValue: string;
			isFavorite?: boolean;
			group?: string;
	  };

type AddressPostalCodeSearchProps = {
	onSelectionChange?: (selectedItem: SearchResult | null) => void;
	customerId?: string | number;
	onlySearchPostalCodes?: boolean;
	onlySearchAddressBook?: boolean;
	showFullAddressOnSelect?: boolean;
	showIcons?: boolean;
	stopType?: number;
	/**
	 * When true, the input is cleared after a selection is committed instead of
	 * displaying the selected item's value. Useful when the selected data is
	 * surfaced in separate fields below the search box, making the in-input
	 * display redundant.
	 */
	clearOnSelect?: boolean;
} & AutoCompleteProps;

export const getAddressBookDisplayValue = (item: AddressBookItemSummary) => {
	const addressParts = [
		item.address1,
		item.address2,
		item.city,
		item.stateProvince,
		item.postalCode,
	].filter(Boolean); // Remove null/undefined values

	const addressString = addressParts.join(", ");
	return item.companyName
		? `${item.companyName} - ${addressString}`
		: addressString;
};

export const AddressPostalCodeSearch = ({
	onSelectionChange,
	customerId,
	onlySearchPostalCodes = false,
	onlySearchAddressBook = false,
	showFullAddressOnSelect = true,
	showIcons = true,
	stopType,
	clearOnSelect = false,
	value: propsValue,
	...props
}: AddressPostalCodeSearchProps) => {
	const [value, setValue] = useState(propsValue?.toString() ?? "");
	const { isInternal } = useDeploymentType();
	const { canManageSharedLists } = useSharedListsAccess();
	const [selectedResult, setSelectedResult] = useState<SearchResult | null>(
		propsValue
			? {
					type: "postalCode",
					data: {} as ZipCode, // Dummy data to prevent clearing on blur
					displayValue: propsValue.toString(),
				}
			: null,
	);
	// Track whether user is actively typing to prevent useEffect from overwriting input
	const isUserTypingRef = useRef(false);
	const hasPendingInputRef = useRef(false);
	// Refs used to commit the highlighted suggestion when the user tabs or
	// clicks away. Kendo clears `state.focusedItem` when the popup closes, so
	// we cache it on `onClose` and read it again on `onBlur`.
	const autoCompleteRef = useRef<AutoCompleteHandle | null>(null);
	const lastFocusedItemRef = useRef<SearchResult | null>(null);
	const wasOpenOnCloseRef = useRef(false);
	// If the user typed something and tabbed/clicked away before the search
	// API responded, remember the typed query so we can commit the first
	// result as soon as it arrives.
	const pendingCommitQueryRef = useRef<string | null>(null);

	// Update internal value when propsValue changes
	React.useEffect(() => {
		if (propsValue === undefined) {
			return;
		}

		const stringValue = propsValue?.toString() ?? "";
		if (
			(isUserTypingRef.current || hasPendingInputRef.current) &&
			!stringValue
		) {
			return;
		}

		isUserTypingRef.current = false;
		hasPendingInputRef.current = false;
		setValue(stringValue);
		setSelectedResult(
			stringValue
				? {
						type: "postalCode",
						data: {} as ZipCode,
						displayValue: stringValue,
					}
				: null,
		);
	}, [propsValue]);

	// Debounce user input
	const [debouncedValue] = useDebounceValue(value, 300);

	const isSearchEnabled = useMemo(() => {
		if (!debouncedValue.trim()) return false;

		// If the debounced value exactly matches our selected result's display value,
		// it means we've already selected an item and don't need to trigger a new search.
		return !(selectedResult && debouncedValue === selectedResult.displayValue);
	}, [debouncedValue, selectedResult]);

	const { userConfigs = [] } = useUserConfiguration();

	const favoriteIds = useMemo(() => {
		return userConfigs
			.filter(
				(config) => config.configType === ConfigurationType.AddressFavorite,
			)
			.map((config) => Number(config.configValue));
	}, [userConfigs]);

	const { data: addressBookItems = [], isFetching: isFetchingAddressBook } =
		useAddressBookSearch(
			debouncedValue,
			customerId,
			isSearchEnabled && !onlySearchPostalCodes && canManageSharedLists,
			isInternal,
		);
	const { data: postalCodes = [], isFetching: isFetchingPostalCodes } =
		usePostalCodeSearch(
			debouncedValue,
			isSearchEnabled && !onlySearchAddressBook,
		);

	const { data: customerAddresses = [] } = useAddressBookItems(
		{ customerId: Number(customerId) },
		!!customerId && canManageSharedLists,
		isInternal,
	);

	const convertedLegacyIds = useMemo(
		() =>
			new Set(
				customerAddresses
					.filter((item) => !item.isLegacy && item.legacyAddressId)
					.map((item) => item.legacyAddressId),
			),
		[customerAddresses],
	);

	const favoriteAddresses = useMemo(() => {
		return customerAddresses
			.filter((item) => {
				if (item.isLegacy && convertedLegacyIds.has(item.addressBookItemId)) {
					return false;
				}
				const isFavorite = favoriteIds.includes(item.addressBookItemId);
				if (!isFavorite) return false;

				// If stopType is provided, only show favorites that have a detail matching that stopType
				if (stopType !== undefined) {
					return item.details?.some((detail) => detail.stopType === stopType);
				}

				return true;
			})
			.slice(0, 5);
	}, [convertedLegacyIds, customerAddresses, favoriteIds, stopType]);

	const isFetching = isFetchingAddressBook || isFetchingPostalCodes;

	// Create postal code results with full data, sorted by country: USA, CAN, MEX.
	// When postal-code searching is disabled (onlySearchAddressBook), ignore any
	// data returned for this query. TanStack Query still surfaces cached results
	// for a matching queryKey even when `enabled` is false (e.g. the ZIP cache
	// populated by a sibling postal-code-only field), so guarding on the data is
	// required to truly suppress postal codes here.
	const postalCodeResults: SearchResult[] = useMemo(
		() =>
			onlySearchAddressBook
				? []
				: postalCodes
						.map((zipCode) => ({
							type: "postalCode" as const,
							data: zipCode,
							displayValue: `${zipCode.postalCode} - ${zipCode.city.name}, ${zipCode.city.state.abbreviation}, ${zipCode.city.state.countryCode}`,
						}))
						.sort(
							(a, b) =>
								(countryOrder[a.data.city.state.countryCode] ?? 99) -
								(countryOrder[b.data.city.state.countryCode] ?? 99),
						),
		[onlySearchAddressBook, postalCodes],
	);

	// Create address book results with full data, sorted by country: USA, CAN, MEX.
	// Mirrors the postal-code guard above: when address-book searching is
	// disabled (onlySearchPostalCodes), ignore any cached data for this query.
	const addressBookResults: SearchResult[] = useMemo(
		() =>
			onlySearchPostalCodes || !canManageSharedLists
				? []
				: addressBookItems
						.filter((item) => {
							if (
								item.isLegacy &&
								convertedLegacyIds.has(item.addressBookItemId)
							) {
								return false;
							}
							if (stopType !== undefined) {
								return item.details?.some(
									(detail) => detail.stopType === stopType,
								);
							}
							return true;
						})
						.map((item) => {
							const displayValue = getAddressBookDisplayValue(item);

							return {
								type: "addressBook" as const,
								data: item,
								displayValue,
								isFavorite: favoriteIds.includes(item.addressBookItemId),
							};
						})
						.sort(
							(a, b) =>
								(countryOrder[a.data.country] ?? 99) -
								(countryOrder[b.data.country] ?? 99),
						),
		[
			addressBookItems,
			canManageSharedLists,
			convertedLegacyIds,
			favoriteIds,
			onlySearchPostalCodes,
			stopType,
		],
	);

	const favoriteResults: SearchResult[] = useMemo(() => {
		if (!canManageSharedLists) return [];
		return favoriteAddresses
			.map((item) => ({
				type: "addressBook" as const,
				data: item,
				displayValue: getAddressBookDisplayValue(item),
				isFavorite: true,
			}))
			.sort(
				(a, b) =>
					(countryOrder[a.data.country] ?? 99) -
					(countryOrder[b.data.country] ?? 99),
			);
	}, [canManageSharedLists, favoriteAddresses]);

	// Combine results: postal codes first, then address book items
	const searchResults = useMemo(
		() => [...postalCodeResults, ...addressBookResults],
		[postalCodeResults, addressBookResults],
	);

	const groupedData = useMemo(() => {
		const results = searchResults.map((r) => ({
			...r,
			group: "Search Results",
		}));

		const favorites = favoriteResults.map((f) => ({
			...f,
			group: "Favorites",
		}));

		// Deduplicate by displayValue. Kendo's AutoComplete uses the textField
		// (displayValue) to track the highlighted item during keyboard
		// navigation, so duplicate display strings break Up/Down arrow keys
		// (the popup gets stuck on the first match or skips items). Duplicates
		// commonly occur when:
		//   - the same address is registered multiple times in the address book
		//   - a favorite address also appears in the search results
		//   - a postal code search returns multiple identical entries
		// Keeping the first occurrence preserves the preferred ordering
		// (Favorites group is appended after Search Results, so a duplicated
		// favorite collapses into its Search Results entry).
		const seen = new Set<string>();
		const deduped: typeof results = [];
		for (const item of [...results, ...favorites]) {
			if (seen.has(item.displayValue)) continue;
			seen.add(item.displayValue);
			deduped.push(item);
		}
		return deduped;
	}, [searchResults, favoriteResults]);

	const handleChange = useCallback(
		(event: { target: { value: string } }) => {
			const selectedValue = event.target.value;
			const hasTypedValue = selectedValue.trim().length > 0;

			// Mark that user is actively typing to prevent useEffect from overwriting
			isUserTypingRef.current = hasTypedValue;

			// Find the corresponding full data object
			const result =
				groupedData.find((r) => r.displayValue === selectedValue) ?? null;

			// Any new keystroke or selection invalidates a prior pending commit
			// (e.g. user typed "1234", tabbed away, came back and changed it).
			pendingCommitQueryRef.current = null;

			// If user made a valid selection, they're done typing
			if (result) {
				isUserTypingRef.current = false;
				hasPendingInputRef.current = false;
				lastFocusedItemRef.current = null;
			} else {
				hasPendingInputRef.current = hasTypedValue;
			}

			// When clearOnSelect is enabled, blank the input after a real
			// selection is committed (the chosen data is shown in fields below).
			const shouldClear = clearOnSelect && result !== null;

			if (result?.type === "postalCode" && !showFullAddressOnSelect) {
				setValue(result.data.postalCode);
			} else {
				setValue(shouldClear ? "" : selectedValue);
			}

			setSelectedResult(shouldClear ? null : result);

			// Notify parent component of selection change.
			//
			// In clearOnSelect mode the input is blanked after a selection, so a
			// subsequent keystroke produces a no-match (null) change event. We must
			// NOT forward that intermediate null to the parent, otherwise the
			// previously-selected address fields would be wiped while the user is
			// merely typing a new search. Only forward when there is an actual
			// match, or when the user has explicitly emptied the input (true clear).
			const isIntermediateTyping =
				clearOnSelect && result === null && hasTypedValue;
			if (!isIntermediateTyping) {
				onSelectionChange?.(result);
			}
		},
		[clearOnSelect, groupedData, onSelectionChange, showFullAddressOnSelect],
	);

	// Commit the currently highlighted suggestion (if any) as the selected
	// value. Used when the user tabs or clicks away from the field while a
	// row is highlighted in the dropdown. If the user has typed a query and
	// the popup is open with results but they haven't explicitly highlighted
	// a row, fall back to the first matching result so tabbing away still
	// commits a meaningful selection.
	const commitFocusedItem = useCallback(() => {
		// Auto-completion only applies to typed input that has not produced a
		// selection yet. Once a suggestion has been committed, the input text is
		// allowed to differ from the selection's display value (e.g. with
		// showFullAddressOnSelect={false} it shows just the postal code), so the
		// guards below cannot tell "already selected" from "typed a query".
		// Running the fallback anyway silently replaces the user's choice with
		// the first search result — picking "33014 - Miami Lakes" and leaving the
		// field would revert to "33014 - Hialeah".
		if (!hasPendingInputRef.current) {
			lastFocusedItemRef.current = null;
			wasOpenOnCloseRef.current = false;
			return;
		}

		const explicitFocusedItem =
			(autoCompleteRef.current?.state?.focusedItem as
				| SearchResult
				| undefined) ?? lastFocusedItemRef.current;
		lastFocusedItemRef.current = null;

		const isOpen =
			autoCompleteRef.current?.state?.opened === true ||
			wasOpenOnCloseRef.current;
		wasOpenOnCloseRef.current = false;

		// Fall back to the first available *search result* (postal code or
		// address book match) when the popup is open with data but nothing is
		// explicitly focused (i.e. the user typed without using arrow keys /
		// hover). Favorites are intentionally excluded from the fallback so we
		// never auto-select a favorite the user didn't actually pick.
		const fallbackItem =
			!explicitFocusedItem && isOpen && value.trim().length > 0
				? searchResults[0]
				: undefined;

		const focusedItem = explicitFocusedItem ?? fallbackItem;
		if (!focusedItem?.displayValue) {
			// Nothing to commit right now. If the user has typed a query that
			// hasn't returned results yet, remember it so the effect below can
			// commit the first result as soon as it arrives.
			const trimmed = value.trim();
			if (trimmed.length > 0 && searchResults.length === 0) {
				pendingCommitQueryRef.current = value;
			}
			return;
		}

		// Don't reapply if the input already shows this item.
		if (value === focusedItem.displayValue) return;
		// Only commit items that are still part of the current results.
		const inData = groupedData.some(
			(r) => r.displayValue === focusedItem.displayValue,
		);
		if (!inData) return;

		handleChange({ target: { value: focusedItem.displayValue } });
	}, [groupedData, handleChange, searchResults, value]);

	const handleClose = useCallback(
		(event: AutoCompleteCloseEvent) => {
			// Cache the highlighted item before Kendo clears `state.focusedItem`
			// as part of closing the popup, so `onBlur` can still read it.
			const focusedItem = autoCompleteRef.current?.state?.focusedItem as
				| SearchResult
				| undefined;
			if (focusedItem) {
				lastFocusedItemRef.current = focusedItem;
			}
			// Remember that the popup was open prior to this close so the blur
			// handler can still treat the user's typed value as a candidate for
			// auto-selection from the visible suggestions.
			wasOpenOnCloseRef.current = true;
			props.onClose?.(event);
		},
		[props],
	);

	const handleBlur = useCallback(
		(event: AutoCompleteBlurEvent) => {
			commitFocusedItem();
			props.onBlur?.(event);
		},
		[commitFocusedItem, props],
	);

	// If the user typed a query and tabbed/clicked away before search results
	// were available, commit the first matching result as soon as it arrives.
	React.useEffect(() => {
		const pending = pendingCommitQueryRef.current;
		if (!pending) return;
		// Wait until the API actually responded for this query, not just any
		// stale result set from a prior search.
		if (debouncedValue !== pending) return;
		if (isFetching) return;
		const first = searchResults[0];
		if (!first) return;
		// Only auto-commit if the input still shows the user's typed query
		// (i.e. they haven't gone back and changed it in the meantime).
		if (value !== pending) {
			pendingCommitQueryRef.current = null;
			return;
		}
		pendingCommitQueryRef.current = null;
		handleChange({ target: { value: first.displayValue } });
	}, [debouncedValue, handleChange, isFetching, searchResults, value]);

	const itemRender = useCallback(
		(li: React.ReactElement, itemProps: { dataItem: SearchResult }) => {
			if (!showIcons) {
				return li;
			}

			const result = itemProps.dataItem;

			return React.cloneElement(
				li,
				li.props as React.HTMLAttributes<HTMLLIElement>,
				<div
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						width: "100%",
						gap: "8px",
					}}
				>
					<span>{result.displayValue}</span>
					<div>
						<Chip
							size={"small"}
							themeColor={result?.type === "postalCode" ? "success" : "info"}
						>
							{result?.type === "postalCode" ? "Postal Code" : "Address"}
						</Chip>
					</div>
				</div>,
			);
		},
		[showIcons],
	);

	const groupHeaderItemRender = useCallback((li: React.ReactElement) => {
		const { style, children, ...props } =
			li.props as React.HTMLAttributes<HTMLLIElement>;
		return React.cloneElement(
			li,
			{
				...props,
				style: {
					...style,
					pointerEvents: "none",
				},
			},
			<b style={{ textAlign: "left" }}>{children}</b>,
		);
	}, []);

	const groupStickyHeaderItemRender = useCallback((div: React.ReactElement) => {
		const { style, children, ...props } =
			div.props as React.HTMLAttributes<HTMLDivElement>;
		return React.cloneElement(
			div,
			{
				...props,
				style: {
					...style,
					pointerEvents: "none",
				},
			},
			<b style={{ textAlign: "left" }}>{children}</b>,
		);
	}, []);

	return (
		<AutoComplete
			{...props}
			ref={autoCompleteRef}
			data={groupedData}
			groupField={onlySearchPostalCodes ? undefined : "group"}
			groupHeaderItemRender={groupHeaderItemRender}
			groupStickyHeaderItemRender={groupStickyHeaderItemRender}
			itemRender={itemRender}
			loading={isFetching}
			onBlur={handleBlur}
			onChange={handleChange}
			onClose={handleClose}
			textField={"displayValue"}
			value={value}
		/>
	);
};
